import {
  Form,
  Input,
  Select,
  Row,
  Col,
  FormInstance,
  Button,
  Alert,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import FloatingFormItem from '@/components/_common/FloatingFormItem';
import CpfFormField from '@/components/_common/CpfFormField';
import {
  CompaniesInfiniteSelectField,
  Group,
  ImagePickerFormField,
  RgFormField,
} from '@/components/_common';
import { InputDate } from '@/components/_common';
import { EmployeeEditFormValues } from '@/core/domain/enterprise/Employees/Employees.types';
import { useEffect } from 'react';
import { removeMask } from '@/utils/mask';
import validateCpf from '@/utils/functions/validateCpf';
import { useImportedCpfCnpj } from '@/core/domain/people/ImportedCPFCNPJ/ImportedCPFCNPJ.hook';
import { useDebounce } from '@/hooks';
import moment from 'moment';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { Check, Copy } from 'react-feather';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';

type Props = {
  form: FormInstance<EmployeeEditFormValues>;
  isEdit?: boolean;
  redirectKey?: string;
};

const EmployeePersonalForm: React.FC<Props> = ({
  form,
  isEdit = false,
  redirectKey,
}) => {
  const intl = useIntl();

  const cpf = Form.useWatch('cpf', form);
  const debounce = useDebounce(cpf);

  const { mutateAsync } = useImportedCpfCnpj();

  const handleCpfCnpj = async (value: string | undefined) => {
    const mask = removeMask(value);
    const isValidCpf = validateCpf(mask);

    if (isEdit) return;

    if (isValidCpf) {
      const data = await mutateAsync(mask);

      form.setFieldsValue({
        name: data.name,
        birthday: moment(data.birthDate),
        gender: data.gender === 'M' ? 'Masculino' : 'Feminino',
      });
    }
  };

  useEffect(() => {
    handleCpfCnpj(debounce);
  }, [debounce]);

  const defaultEmptyFieldError = {
    required: true,
    message: intl.formatMessage({
      id: 'commons.form.errors.requiredField',
    }),
  };

  const { isCopied: isLinkCopied, copyToClipboard } = useCopyToClipboard();

  return (
    <Form name="employeePersonal" layout="vertical" form={form}>
      <Form.Item hidden name="id" />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={16}>
          <FloatingFormItem
            name="name"
            rules={[defaultEmptyFieldError]}
            label={
              <FormattedMessage id="employee.personal.form.employeeName" />
            }
          >
            <Input />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            name="gender"
            label={<FormattedMessage id="employee.personal.form.gender" />}
          >
            <Select style={{ width: '100%' }}>
              <Select.Option value="Masculino">Masculino</Select.Option>
              <Select.Option value="Feminino">Feminino</Select.Option>
            </Select>
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <CpfFormField
            required
            fieldName="cpf"
            label={intl.formatMessage({ id: 'employee.personal.form.cpf' })}
          />
        </Col>

        <Col xs={24} sm={12} md={8}>
          <RgFormField />
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            name="birthday"
            label={<FormattedMessage id="employee.personal.form.birth" />}
          >
            <InputDate />
          </FloatingFormItem>
        </Col>

        <Col xs={24}>
          <FloatingFormItem
            name="companiesIds"
            rules={[defaultEmptyFieldError]}
            label={<FormattedMessage id="employee.personal.form.companies" />}
          >
            <CompaniesInfiniteSelectField
              mode="multiple"
              queryFilter={{
                active: undefined,
                isCompanyFilter: false,
                rows: 50,
              }}
              queryEnable
              buttonSelectAll
              selectAll={!isEdit}
            />
          </FloatingFormItem>
        </Col>

        {!!redirectKey &&
          hasPermissions([
            Authorities.ENABLE_EMPLOYEE_ACCESS_IN_YARD_MANAGEMENT,
          ]) && (
            <Col span={24}>
              <Row gutter={24}>
                <Col flex={1}>
                  <FloatingFormItem
                    name="redirectKey"
                    label="Link para Gestão de Pátio"
                    style={{ width: '100%' }}
                  >
                    <Input disabled />
                  </FloatingFormItem>
                </Col>

                <Col>
                  <Button
                    disabled={isLinkCopied}
                    size="large"
                    onClick={() =>
                      copyToClipboard(
                        `${
                          process.env.REACT_APP_GP_URL
                        }/productive?guid=${encodeURIComponent(redirectKey)}`,
                        'Link compiado para área de transferência',
                        'Não foi possível copiara o link',
                      )
                    }
                  >
                    <Group>
                      {isLinkCopied ? <Check size={18} /> : <Copy size={18} />}
                      <span>
                        {isLinkCopied ? 'Link copiado' : 'Copiar link'}
                      </span>
                    </Group>
                  </Button>
                </Col>
              </Row>
              <Alert
                type="warning"
                description={
                  <>
                    <strong style={{ fontSize: '1.1rem' }}>
                      Acesso à Gestão de Pátio
                    </strong>
                    <p>
                      Foi disponibilizado um <strong>link exclusivo</strong>{' '}
                      para que o funcionário acompanhe as atividades na
                      plataforma Gestão de Pátio. Ao acessar o link, o
                      funcionário terá acesso às tarefas programadas para o dia,
                      visualizando apenas os serviços que foram previamente
                      adicionados ao diário do pátio.
                    </p>
                    <p>Compartilhe o link somente ao funcionário destinado</p>
                    <p>
                      <strong>O Código de acesso é o CPF do funcionário</strong>
                    </p>
                    <p>Funções disponíveis:</p>
                    <ul>
                      <li>
                        Visualizar as atividades relacionadas ao funcionário
                      </li>
                      <li>Gerenciar o tempo de serviço das atividades</li>
                      <li>Finalizar os serviços das atividades</li>
                    </ul>
                  </>
                }
              />
            </Col>
          )}

        <Col xs={24} sm={24} md={16}>
          <FloatingFormItem
            name="observation"
            label={<FormattedMessage id="employee.personal.form.observation" />}
          >
            <Input.TextArea
              rows={3}
              style={{ height: '102px' }}
              maxLength={240}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <ImagePickerFormField
            norificationSuccessHide
            maxFileCount={1}
            fieldName="photo"
            label={intl.formatMessage({
              id: 'employee.personal.form.upload',
            })}
          />
        </Col>
      </Row>
    </Form>
  );
};

export default EmployeePersonalForm;
